import {
  differenceInCalendarDays,
  eachDayOfInterval,
  isValid,
  isWithinInterval,
  startOfDay,
} from 'date-fns'
import React from 'react'
import { dateAdjustedForTimezone } from '@opallabs/lambda'
import {
  PartialContent,
  PartialMoment,
  contentHasScheduledAt,
  contentHasTakedown,
} from '../momentTypes'
import { WeekViewSortType } from '../types'
import { makeContentCategoryGetter } from './contentGetters'
import {
  addValueToMap,
  createMomentMap,
  formatDateKey,
  makeMapKey,
  MomentMap as ContentIdMap,
} from './momentMap'

/**
 * Content-primary map: Map<date+category, content_id[]>. Keyed by each content
 * item's own `scheduled_at`, not its parent moment's.
 */
export type ContentMap = ContentIdMap

/**
 * Post rendered as a horizontal bar at the row level (not in contentMap).
 * Posts with a takedown span their in-market range; posts without
 * collapse to a 1-col bar on their scheduled day. Clamped flags mark
 * edges whose range extends past the visible week.
 */
export type ContentSpanItem = {
  id: string
  categoryId: string | null
  startDayIndex: number
  dayCount: number
  clampedLeft: boolean
  clampedRight: boolean
}

interface ContentMapProviderParams {
  interval: {
    start: Date
    end: Date
  }
  content: Record<string, PartialContent> | null | undefined
  moments: Record<string, PartialMoment> | null | undefined
  groupBy: WeekViewSortType
  // Parent entity used for more specific grouping (e.g. selected label set)
  parentCategory?: string
  // When true every post is lifted to `spanItems` (bar rendering).
  // When false posts key to their scheduled_at day via `contentMap`.
  spanInMarketDays?: boolean
}

type ContentMapContextValue = {
  categoryCounts: Map<string | null, number>
  contentMap: ContentMap
  dayCounts: Map<string, number>
  getContent: (id: string) => PartialContent | undefined
  getMomentForContent: (contentId: string) => PartialMoment | undefined
  // Raw moments map for consumers (e.g. the attach-to-existing-moment
  // picker) that need to list or filter moments directly. Prefer
  // `getMomentForContent` for content-scoped lookups.
  moments: Record<string, PartialMoment> | null | undefined
  spanItems: ContentSpanItem[]
}

export const ContentMapContext = React.createContext<
  ContentMapContextValue | undefined
>(undefined)

const incrementMapCount = (
  map: Map<string | null, number>,
  key: string | null
) => {
  const currentCount = map.get(key) ?? 0
  map.set(key, currentCount + 1)
}

const momentIdForContent = (content: PartialContent): string | undefined =>
  content.relationships?.moment?.id

// Pull optimistic "duplicating..." posts out of moments so Content mode can
// render them before the server responds.
//
// `DUPLICATE_CONTENT` in `../reducer.js` pushes a new post with `id: null,
// isSaving: true, uuid: <fresh>` into `state.moments[x].posts`. Moment mode
// iterates `moment.posts` directly so the row appears immediately; Content
// mode reads from the Redux `content` slice, which has no such entry until
// the server response lands. We bridge that gap by surfacing the optimistic
// rows as synthetic content entries keyed by their uuid.
const collectOptimisticPosts = (
  moments: Record<string, PartialMoment> | null | undefined
): Record<string, PartialContent> => {
  if (!moments) return {}
  const optimistic: Record<string, PartialContent> = {}
  Object.values(moments).forEach((moment) => {
    const posts = (moment as unknown as { posts?: unknown[] }).posts
    if (!Array.isArray(posts)) return
    posts.forEach((post) => {
      const candidate = post as {
        id?: string | null
        uuid?: string
        isSaving?: boolean
        name?: string | null
      } & PartialContent
      if (candidate.id != null || !candidate.isSaving || !candidate.uuid) return
      // Match the server-side PostDuplicator, which appends `' (copy)'`
      // unless the name already contains it (see
      // `post_duplicator.rb#duplicate_basic_attributes`). The WeekView
      // reducer does the same when producing `newContent`, but Content
      // mode's duplicate source can have an empty/nullish `name` on the
      // Redux content slice, which short-circuits the reducer's append.
      // Re-applying the suffix here keeps the optimistic card visually
      // consistent with whatever the server will return after save.
      const baseName =
        typeof candidate.name === 'string' && candidate.name.length > 0
          ? candidate.name
          : 'Untitled content'
      const name = baseName.includes('(copy)') ? baseName : `${baseName} (copy)`
      optimistic[candidate.uuid] = {
        ...candidate,
        name,
        // Synthetic id so the downstream map/getContent lookups (which key
        // by `id`) can find this row. Once the real post lands in the
        // content slice with a server-assigned id, BOOTSTRAP_WEEK_VIEW
        // replaces `state.moments`, clearing this placeholder.
        id: candidate.uuid,
        relationships: candidate.relationships ?? {
          moment: { id: moment.id },
        },
      }
    })
  })
  return optimistic
}

export const ContentMapProvider: React.FC<
  React.PropsWithChildren<ContentMapProviderParams>
> = ({
  children,
  content,
  groupBy,
  interval,
  moments,
  parentCategory,
  spanInMarketDays = false,
}) => {
  const mergedContent = React.useMemo(() => {
    const optimistic = collectOptimisticPosts(moments)
    if (Object.keys(optimistic).length === 0) return content ?? null
    return { ...(content ?? {}), ...optimistic }
  }, [content, moments])

  const {
    map: contentMap,
    dayCounts,
    categoryCounts,
    spanItems,
  } = React.useMemo(() => {
    const categoryCounts = new Map<string | null, number>()
    const dayCounts = new Map<string, number>()
    const map = createMomentMap()
    const spanItems: ContentSpanItem[] = []
    if (!mergedContent) return { categoryCounts, dayCounts, map, spanItems }

    const categoryGetter = makeContentCategoryGetter(groupBy)
    const intervalStart = startOfDay(interval.start)

    Object.values(mergedContent).forEach((item) => {
      if (!contentHasScheduledAt(item)) return
      const date = dateAdjustedForTimezone(item.scheduled_at)
      if (date === null || !isValid(date)) {
        // Skip malformed entries instead of crashing the entire
        // ContentMapContext subtree. Mirrors `momentMap`'s tolerance and
        // keeps a single bad serializer row from blanking the grid.
        console.error('Content has invalid `scheduled_at`, skipping', {
          contentId: item?.id,
          scheduled_at: item?.scheduled_at,
        })
        return
      }

      const momentId = momentIdForContent(item)
      const parentMoment = momentId && moments ? moments[momentId] : undefined
      const itemCategories = categoryGetter(item, parentMoment, parentCategory)

      if (spanInMarketDays) {
        const hasTakedown = contentHasTakedown(item)
        const endDate = hasTakedown
          ? dateAdjustedForTimezone(item.takedown_scheduled_at)
          : date
        if (endDate === null || !isValid(endDate)) return

        if (endDate < interval.start || date > interval.end) return

        const clampedLeft = hasTakedown && date < interval.start
        const clampedRight = hasTakedown && endDate > interval.end
        const visibleStart = clampedLeft ? interval.start : date
        const visibleEnd = clampedRight ? interval.end : endDate

        const days = eachDayOfInterval({
          start: startOfDay(visibleStart),
          end: startOfDay(visibleEnd),
        })
        const startDayIndex = differenceInCalendarDays(
          startOfDay(visibleStart),
          intervalStart
        )
        const dayCount = days.length
        if (dayCount < 1) return

        const registerSpan = (categoryId: string | null) => {
          incrementMapCount(categoryCounts, categoryId)
          days.forEach((day) => {
            incrementMapCount(dayCounts, formatDateKey(day))
          })
          spanItems.push({
            id: item.id,
            categoryId,
            startDayIndex,
            dayCount,
            clampedLeft,
            clampedRight,
          })
        }

        if (itemCategories === null) registerSpan(null)
        else itemCategories.forEach(registerSpan)
        return
      }

      if (!isWithinInterval(date, interval)) return

      const dateKey = formatDateKey(date)
      incrementMapCount(dayCounts, dateKey)

      if (itemCategories === null) {
        const key = makeMapKey(date, null)
        incrementMapCount(categoryCounts, null)
        addValueToMap(map, key, item.id)
      } else {
        itemCategories.forEach((categoryId) => {
          const key = makeMapKey(date, categoryId)
          incrementMapCount(categoryCounts, categoryId)
          addValueToMap(map, key, item.id)
        })
      }
    })

    return { categoryCounts, dayCounts, map, spanItems }
  }, [
    mergedContent,
    moments,
    groupBy,
    interval,
    parentCategory,
    spanInMarketDays,
  ])

  // Stable identities so the many content-card consumers don't re-render on
  // every WeekView render (drag, search keystroke, settings toggle).
  const getContent = React.useCallback(
    (id: string) => (mergedContent ? mergedContent[id] : undefined),
    [mergedContent]
  )
  const getMomentForContent = React.useCallback(
    (contentId: string) => {
      const item = mergedContent?.[contentId]
      if (!item) return undefined
      const momentId = momentIdForContent(item)
      return momentId && moments ? moments[momentId] : undefined
    },
    [mergedContent, moments]
  )

  const value = React.useMemo(
    () => ({
      categoryCounts,
      contentMap,
      dayCounts,
      getContent,
      getMomentForContent,
      moments,
      spanItems,
    }),
    [
      categoryCounts,
      contentMap,
      dayCounts,
      getContent,
      getMomentForContent,
      moments,
      spanItems,
    ]
  )

  return (
    <ContentMapContext.Provider value={value}>
      {children}
    </ContentMapContext.Provider>
  )
}

export const useContentMap = (): ContentMapContextValue => {
  const context = React.useContext(ContentMapContext)
  if (context === undefined) {
    throw new Error(
      'useContentMap must be called from within a ContentMapProvider.'
    )
  }
  return context
}
